import { useCallback, useEffect, useRef, useState } from 'react';
import { AppIcon, Mark, Wordmark } from '../brand/brand';
import { session } from '../lib/storage';
import { useSettings } from '../state/settings';
import { useToasts } from '../state/toasts';
import { APPS } from './apps';
import { Boot, ShutdownScreen } from './Boot';
import { DesktopIcons } from './DesktopIcons';
import { useMotionClass } from './motion';
import { stopAll } from './processes';
import { StartMenu, Taskbar } from './Taskbar';
import { Wallpaper } from './Wallpaper';
import { Window } from './Window';
import { openApp, savedSession, useWm } from './wm';
import { hasUnsavedChanges } from './unsaved';
import { useCoordinator } from '../state/coordinator';
import { useWallet, shortAddr } from '../state/wallet';

const PHONE_MAX = 640;

const Windows = () => {
  const ids = useWm((s) => Object.keys(s.wins).join(','));
  return (
    <div className="windows">
      {ids
        .split(',')
        .filter(Boolean)
        .map((id) => (
          <Window key={id} id={id as keyof typeof APPS} />
        ))}
    </div>
  );
};

function Toasts() {
  const toasts = useToasts((s) => s.toasts);
  return (
    <div className="toasts" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={`toast ${t.kind}${t.leaving ? ' out' : ''}`}>
          <span>{t.text}</span>
          <button aria-label="Dismiss" onClick={() => useToasts.getState().dismiss(t.id)}>
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

function Switcher({ onClose }: { onClose: () => void }) {
  const order = useWm((s) => s.order);
  return (
    <div className="switcher" onClick={onClose} role="dialog" aria-label="Open apps">
      {order.length === 0 && <div className="switcher-card">No apps open. Tap an icon to start one.</div>}
      {order.map((id) => (
        <div key={id} className="row nowrap" onClick={(e) => e.stopPropagation()}>
          <button
            className="switcher-card grow"
            onClick={() => {
              useWm.getState().restore(id);
              onClose();
            }}
          >
            <AppIcon name={APPS[id].icon} size={30} />
            <span className="grow">{APPS[id].title}</span>
          </button>
          <button className="btn" aria-label={`Close ${APPS[id].title}`} onClick={() => useWm.getState().requestClose(id)}>
            Close
          </button>
        </div>
      ))}
    </div>
  );
}

function ContextMenu({ x, y, onClose }: { x: number; y: number; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => ref.current?.querySelector('button')?.focus(), []);
  const item = (label: string, fn: () => void) => (
    <button
      role="menuitem"
      onClick={() => {
        onClose();
        fn();
      }}
    >
      {label}
    </button>
  );
  return (
    <div ref={ref} className="ctx-menu" role="menu" style={{ left: Math.min(x, innerWidth - 200), top: Math.min(y, innerHeight - 170) }}>
      {item('Auto-arrange icons', () => window.dispatchEvent(new Event('kumio:auto-arrange')))}
      {item('Change wallpaper…', () => openApp('settings'))}
      {item('Task manager', () => openApp('taskmgr'))}
      {item('Open readme.txt', () => openApp('readme'))}
    </div>
  );
}

export function Desktop() {
  useMotionClass();
  const wallpaper = useSettings((s) => s.wallpaper);
  const scan = useSettings((s) => s.scanlines);
  const phone = useWm((s) => s.phone);
  const hasOpenWindow=useWm(s=>Object.values(s.wins).some(w=>w&&!w.minimized&&w.phase==='open'));
  const config=useCoordinator(s=>s.config);
  const address=useWallet(s=>s.session?.address||s.connected);
  const [booting, setBooting] = useState(() => !(useSettings.getState().skipBoot || session.get('booted')));
  const [off, setOff] = useState(false);
  const [reveal, setReveal] = useState(false);
  const [start, setStart] = useState<'closed' | 'open' | 'closing'>('closed');
  const [ctx, setCtx] = useState<{ x: number; y: number } | null>(null);
  const [switcher, setSwitcher] = useState(false);
  const [hidden, setHidden] = useState(false);
  const deskRef = useRef<HTMLElement>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    document.documentElement.dataset.wall = wallpaper;
    const meta = document.querySelector('meta[name="theme-color"]');
    meta?.setAttribute('content', wallpaper === 'mint' ? '#F4E6DC' : '#F3EBDD');
  }, [wallpaper]);
  useEffect(() => {
    document.documentElement.classList.toggle('no-scan', !scan);
  }, [scan]);

  // Desktop area + phone mode
  useEffect(() => {
    const el = deskRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      useWm.getState().setArea({ w: Math.round(r.width), h: Math.round(r.height) });
      useWm.getState().setPhone(innerWidth <= PHONE_MAX);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [off]);

  // Pause decorative motion in background tabs
  useEffect(() => {
    const fn = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', fn);
    return () => document.removeEventListener('visibilitychange', fn);
  }, []);

  const afterBoot = useCallback(() => {
    session.set('booted', '1');
    setBooting(false);
    setReveal(true);
    setTimeout(() => setReveal(false), 700);
    if (startedRef.current) return;
    startedRef.current = true;
    const s = useSettings.getState();
    if (!s.seenWelcome) {
      s.set('seenWelcome', true);
      // New visitors start on the approved desktop. Compute only starts manually.
    } else {
      for (const w of savedSession()) {
        openApp(w.id);
        if (w.min) useWm.getState().minimize(w.id);
      }
    }
  }, []);
  useEffect(() => {
    if (!booting && !startedRef.current) afterBoot();
  }, [booting, afterBoot]);

  const closeStart = useCallback(() => {
    setStart((s) => (s === 'open' ? 'closing' : s));
    setTimeout(() => setStart((s) => (s === 'closing' ? 'closed' : s)), 120);
  }, []);
  const toggleStart = () => (start === 'open' ? closeStart() : setStart('open'));

  // Escape, outside click, OS shortcuts (Alt+Shift chords avoid browser-native combos)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (start === 'open') {
          closeStart();
          document.getElementById('start-button')?.focus();
        }
        setCtx(null);
        setSwitcher(false);
        return;
      }
      if (e.altKey && e.shiftKey) {
        const st = useWm.getState();
        const f = st.focused;
        if (e.code === 'KeyK') {
          e.preventDefault();
          toggleStart();
        } else if (f && e.code === 'KeyM') {
          e.preventDefault();
          st.minimize(f);
        } else if (f && e.code === 'ArrowUp') {
          e.preventDefault();
          st.toggleMaximize(f);
        } else if (f && e.code === 'KeyW') {
          e.preventDefault();
          st.requestClose(f);
        }
      }
    };
    const onDown = (e: PointerEvent) => {
      const t = e.target as HTMLElement;
      if (start === 'open' && !t.closest('#start-menu') && !t.closest('#start-button')) closeStart();
      if (!t.closest('.ctx-menu')) setCtx(null);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onDown);
    };
  });

  const shutdown = async () => {
    if(hasUnsavedChanges()&&!window.confirm('You have unsaved changes. Discard them and shut down NimoOS?'))return;
    closeStart();
    await stopAll();
    useWm.getState().closeAll();
    setOff(true);
  };
  const restart = () => {
    setOff(false);
    startedRef.current = false;
    setBooting(true);
  };

  if (off) return <ShutdownScreen onRestart={restart} />;

  return (
    <div className={['os', phone && 'phone', reveal && 'reveal', hidden && 'paused',hasOpenWindow&&'nimu-app-open'].filter(Boolean).join(' ')}>
      <header className="nimu-topbar"><button className="nimu-brand-button" aria-label="Show NimoOS desktop" onClick={()=>{for(const id of useWm.getState().order)useWm.getState().minimize(id);}}><Mark size={36}/><Wordmark height={30}/></button><nav aria-label="Website navigation"><button onClick={()=>{for(const id of useWm.getState().order)useWm.getState().minimize(id);}}>Desktop</button><button onClick={()=>setStart('open')}>Apps</button><button onClick={()=>openApp('readme')}>Docs</button>{config?.socialUrl&&<a href={config.socialUrl} target="_blank" rel="noopener noreferrer">{config.socialLabel||'X'}</a>}</nav><button className="nimu-wallet-link" onClick={()=>openApp('wallet')}><AppIcon name="wallet" size={24}/><span>{address?shortAddr(address):'Connect wallet'}</span></button></header>
      <main
        ref={deskRef}
        className="desktop"
        onContextMenu={(e) => {
          if ((e.target as HTMLElement).closest('.win')) return;
          e.preventDefault();
          setCtx({ x: e.clientX, y: e.clientY });
        }}
      >
        <Wallpaper />
        <DesktopIcons />
        <Windows />
        {switcher && <Switcher onClose={() => setSwitcher(false)} />}
      </main>
      {start !== 'closed' && <StartMenu closing={start === 'closing'} onClose={closeStart} onShutdown={shutdown} />}
      {ctx && <ContextMenu x={ctx.x} y={ctx.y} onClose={() => setCtx(null)} />}
      <Toasts />
      <Taskbar startOpen={start === 'open'} onToggleStart={toggleStart} onSwitcher={() => setSwitcher((v) => !v)} />
      {booting && <Boot onDone={afterBoot} />}
    </div>
  );
}
